import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMagnetic } from '../hooks/useMagnetic';

// Lexicon is intentionally NOT in the top nav — it exists purely as an SEO
// surface (still reachable via footer, routes, and sitemap). Explore and
// Corporate Travel are real routes, so crawlers can follow them from every page.
const navLinks = [
  { label: 'Voyara AI', path: '#home' },
  { label: 'Vision', path: '#vision' },
  { label: 'Technology', path: '#technology' },
  { label: 'Explore', path: '/explore' },
  { label: 'Corporate Travel', path: '/corporate-travel' },
  { label: 'Early Access', path: '#waitlist' }
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileOpen, setMobileOpen] = useState(false);
  const magnetic = useMagnetic(0.15);
  const { pathname } = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50);

      // Scroll Spy Logic
      const sections = ['home', 'vision', 'technology', 'waitlist'];
      const scrollPos = window.scrollY + 120; // Offset for navbar

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const height = element.offsetHeight;
          if (scrollPos >= offsetTop && scrollPos < offsetTop + height) {
            setActiveSection(sectionId);
          }
        }
      }
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    if (path.startsWith('#')) {
      if (pathname === '/') {
        e.preventDefault();
        const id = path.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          const offset = 100; // Account for navbar height
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }
      if (mobileOpen) setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Top scrim — a soft black-to-transparent fade behind the floating nav
          so hero content passing underneath never visually collides with it. */}
      <div
        className="fixed top-0 left-0 right-0 h-32 z-40 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0) 100%)',
        }}
        aria-hidden="true"
      />

      <nav
        className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-700 ${
          scrolled ? 'w-[92%] max-w-[1000px]' : 'w-[95%] max-w-[1200px]'
        }`}
      >
        <div className={`rounded-full px-8 flex items-center justify-between h-[64px] border transition-all duration-700 ${
          scrolled
            ? 'border-white/[0.08] bg-black/50 backdrop-blur-2xl shadow-[0_8px_50px_rgba(0,0,0,0.65)]'
            : 'border-white/[0.04] bg-white/[0.03] shadow-[0_4px_40px_rgba(0,0,0,0.5)]'
        }`}>
          {/* Left - Logo */}
          <Link
            to="/"
            className="font-serif text-xl tracking-tight text-primary hover:text-white transition-colors"
          >
            Eclipse Group
          </Link>

          {/* Center - Nav Links (Desktop) */}
          <nav className="hidden lg:flex items-center justify-center absolute left-1/2 -translate-x-1/2 h-full" aria-label="Main Navigation">
            <ul className="flex items-center gap-4 xl:gap-7 h-full p-0 m-0 list-none">
              {navLinks.map((link) => {
                const isHash = link.path.startsWith('#');
                const isActive = isHash 
                  ? (pathname === '/' && activeSection === link.path.replace('#', ''))
                  : (pathname === link.path || pathname.startsWith(link.path));
                return (
                  <li key={link.label} className="h-full flex items-center">
                    <Link
                      to={isHash ? `/${link.path}` : link.path}
                      onClick={(e) => handleScroll(e, link.path)}
                      className={`relative text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors duration-300 cursor-pointer h-full flex items-center group ${
                        isActive ? 'text-primary' : 'text-primary/50 hover:text-primary/90'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className="relative z-10">{link.label}</span>
                      {isActive && (
                        <div
                          className="absolute bottom-[20px] left-0 right-0 h-[1px] bg-white/50 shadow-[0_0_10px_rgba(255,255,255,0.4)]"
                        />
                      )}
                      {/* Subtle glow on hover/active */}
                      <div className={`absolute inset-0 bg-blue-500/5 blur-xl rounded-full transition-opacity duration-300 pointer-events-none ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Right - CTA */}
          <div className="flex items-center gap-4">
            <div
              ref={magnetic.ref}
              onMouseMove={magnetic.handleMouseMove}
              onMouseLeave={magnetic.handleMouseLeave}
            >
              <Link
                to={pathname === '/' ? '#waitlist' : '/#waitlist'}
                onClick={(e) => handleScroll(e, '#waitlist')}
                className="hidden sm:flex items-center justify-center h-[38px] text-[12px] font-bold tracking-wider uppercase px-6 rounded-full bg-white/10 border border-white/20 text-primary hover:bg-white/15 hover:text-white transition-all duration-300 cursor-pointer"
              >
                Request Access
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden flex flex-col items-center justify-center gap-1.5 cursor-pointer w-11 h-11 rounded-full border border-white/10 bg-white/5 active:scale-95 transition-all outline-none"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              <span
                className={`block w-5 h-[1.5px] bg-white/80 transition-all duration-300 ${
                  mobileOpen ? 'rotate-45 translate-y-[5px]' : ''
                }`}
              />
              <span
                className={`block w-5 h-[1.5px] bg-white/80 transition-all duration-300 ${
                  mobileOpen ? 'opacity-0 scale-x-0' : ''
                }`}
              />
              <span
                className={`block w-5 h-[1.5px] bg-white/80 transition-all duration-300 ${
                  mobileOpen ? '-rotate-45 -translate-y-[5px]' : ''
                }`}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="fixed top-24 left-1/2 -translate-x-1/2 w-[90%] max-w-md max-h-[calc(100dvh-7rem)] overflow-y-auto z-50 liquid-glass-strong rounded-3xl p-8"
        >
          <div className="flex flex-col gap-6">
            {navLinks.map((link) => {
              const isHash = link.path.startsWith('#');
              return (
                <Link
                  key={link.label}
                  to={isHash ? `/${link.path}` : link.path}
                  onClick={(e) => handleScroll(e, link.path)}
                  className="text-lg font-medium text-primary/70 hover:text-primary transition-colors text-left cursor-pointer"
                >
                  {link.label}
                </Link>
              );
            })}
            <Link
              to={pathname === '/' ? '#waitlist' : '/#waitlist'}
              onClick={(e) => handleScroll(e, '#waitlist')}
              className="mt-4 text-center text-sm font-bold tracking-wider uppercase px-8 py-4 rounded-full bg-white/10 border border-white/20 text-primary hover:bg-white/15 transition-all cursor-pointer"
            >
              Request Access
            </Link>
          </div>
        </div>
      )}
      
    </>
  );
}
